class CartBundleCarousel extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleNavigation);
    this.addEventListener('change', this.handleVariantChange);
    this.preloadAround(0);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleNavigation);
    this.removeEventListener('change', this.handleVariantChange);
  }

  handleNavigation = (event) => {
    const button = event.target.closest('[data-bundle-nav]');
    if (!button || !this.contains(button)) return;

    const carousel = this.querySelector('.cart-drawer__look-carousel');
    const cards = [...this.querySelectorAll('.cart-drawer__look-card')];
    if (!carousel || cards.length < 2) return;

    const rtl = getComputedStyle(carousel).direction === 'rtl';
    const edge = rtl ? 'right' : 'left';
    const carouselEdge = carousel.getBoundingClientRect()[edge];
    let currentIndex = 0;
    let nearestDistance = Infinity;
    cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect()[edge] - carouselEdge);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        currentIndex = index;
      }
    });
    const direction = button.dataset.bundleNav === 'next' ? 1 : -1;
    const targetIndex = (currentIndex + direction + cards.length) % cards.length;
    this.preloadAround(targetIndex);
    const delta = cards[targetIndex].getBoundingClientRect()[edge] - carouselEdge;
    const wraps = Math.abs(targetIndex - currentIndex) > 1;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    carousel.scrollTo({ left: carousel.scrollLeft + delta, behavior: wraps || reduceMotion ? 'instant' : 'smooth' });
  };

  handleVariantChange = (event) => {
    if (!(event.target instanceof Element)) return;
    const select = event.target.closest('.cart-drawer__look-variant');
    if (!(select instanceof HTMLSelectElement) || !this.contains(select)) return;

    const option = select.options[select.selectedIndex];
    const card = select.closest('.cart-drawer__look-card');
    const image = card?.querySelector('.cart-drawer__look-image img');
    const imageUrl = option?.dataset.imageUrl;

    if (!(image instanceof HTMLImageElement) || !imageUrl) return;

    image.removeAttribute('srcset');
    image.src = imageUrl;
    image.alt = option.dataset.imageAlt || image.alt;
  };

  preloadAround(index) {
    const cards = [...this.querySelectorAll('.cart-drawer__look-card')];
    if (!cards.length) return;

    [index - 1, index, index + 1].forEach((candidateIndex) => {
      const normalizedIndex = (candidateIndex + cards.length) % cards.length;
      const image = cards[normalizedIndex]?.querySelector('.cart-drawer__look-image img');
      if (image instanceof HTMLImageElement) image.loading = 'eager';
    });
  }
}

if (!customElements.get('cart-bundle-carousel')) {
  customElements.define('cart-bundle-carousel', CartBundleCarousel);
}

class CartBundleRecommendations extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.loadRecommendations();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  async loadRecommendations() {
    const track = this.querySelector('[data-bundle-recommendations-track]');
    const url = this.dataset.url;
    const sectionId = this.dataset.sectionId;
    const productIds = [...new Set((this.dataset.productIds || '').split(',').filter(Boolean))];
    const cartProductIds = new Set(productIds);
    const limit = Math.max(1, Number.parseInt(this.dataset.limit || '16', 10));

    if (!(track instanceof HTMLElement) || !url || !sectionId || productIds.length === 0) return;

    try {
      const responses = await Promise.all(
        productIds.slice(0, 4).map((productId) => {
          const requestUrl = new URL(url, window.location.origin);
          requestUrl.searchParams.set('section_id', sectionId);
          requestUrl.searchParams.set('product_id', productId);
          requestUrl.searchParams.set('limit', String(Math.min(limit, 10)));
          requestUrl.searchParams.set('intent', 'related');

          return fetch(requestUrl, { signal: this.controller.signal })
            .then((response) => {
              if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
              return response.text();
            })
            .catch((error) => {
              if (error.name === 'AbortError') throw error;
              console.error('Cart bundle recommendations request error:', error);
              return '';
            });
        })
      );

      const seenProductIds = new Set();
      const cards = [];

      for (const html of responses) {
        if (!html) continue;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');

        for (const card of documentFragment.querySelectorAll('[data-cart-bundle-card]')) {
          const productId = card.dataset.productId;
          if (!productId || cartProductIds.has(productId) || seenProductIds.has(productId)) continue;

          seenProductIds.add(productId);
          cards.push(card);
          if (cards.length >= limit) break;
        }

        if (cards.length >= limit) break;
      }

      if (cards.length === 0) return;

      track.replaceChildren(...cards);
      this.hidden = false;
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Cart bundle recommendations error:', error);
    }
  }
}

if (!customElements.get('cart-bundle-recommendations')) {
  customElements.define('cart-bundle-recommendations', CartBundleRecommendations);
}
